// Module Analytics Avancés - Interface Admin
import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { FiBarChart2, FiTrendingUp, FiDownload, FiCalendar, FiFilter, FiPieChart, FiActivity } from 'react-icons/fi';

const Container = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing.xl};`;
const Header = styled.div`background: white; padding: ${theme.spacing.xl}; border-radius: ${theme.borderRadius.lg}; box-shadow: ${theme.shadows.sm}; display: flex; justify-content: space-between; align-items: start;`;
const Title = styled.h1`font-size: ${theme.typography.sizes['2xl']}; font-weight: ${theme.typography.weights.bold}; color: ${theme.colors.text}; margin: 0 0 ${theme.spacing.xs} 0;`;
const Subtitle = styled.p`font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}60; margin: 0;`;
const FilterBar = styled.div`display: flex; gap: ${theme.spacing.md}; align-items: center;`;
const Select = styled.select`padding: ${theme.spacing.sm} ${theme.spacing.md}; border: 1px solid ${theme.colors.surface}; border-radius: ${theme.borderRadius.md}; font-family: ${theme.typography.fontFamily}; font-size: ${theme.typography.sizes.sm}; background: white; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;
const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`padding: ${theme.spacing.md} ${theme.spacing.lg}; border: none; border-radius: ${theme.borderRadius.md}; font-family: ${theme.typography.fontFamily}; font-size: ${theme.typography.sizes.sm}; font-weight: ${theme.typography.weights.semibold}; cursor: pointer; display: flex; align-items: center; gap: ${theme.spacing.sm}; transition: all ${theme.transitions.fast}; ${props => props.variant === 'secondary' ? `background: transparent; color: ${theme.colors.text}; border: 1px solid ${theme.colors.surface}; &:hover { background: ${theme.colors.surface}; }` : `background: ${theme.colors.primary}; color: white; &:hover { background: ${theme.colors.primary}DD; transform: translateY(-2px); }`}`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: ${theme.spacing.lg};`;
const Card = styled.div`background: white; padding: ${theme.spacing.xl}; border-radius: ${theme.borderRadius.lg}; box-shadow: ${theme.shadows.sm};`;
const CardTitle = styled.h3`font-size: ${theme.typography.sizes.lg}; font-weight: ${theme.typography.weights.semibold}; color: ${theme.colors.text}; margin: 0 0 ${theme.spacing.lg} 0; display: flex; align-items: center; gap: ${theme.spacing.sm};`;
const StatValue = styled.div`font-size: ${theme.typography.sizes['3xl']}; font-weight: ${theme.typography.weights.bold}; color: ${theme.colors.text}; margin-bottom: ${theme.spacing.xs};`;
const StatLabel = styled.div`font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}60;`;
const Trend = styled.div<{ positive?: boolean }>`display: flex; align-items: center; gap: ${theme.spacing.xs}; font-size: ${theme.typography.sizes.sm}; font-weight: ${theme.typography.weights.semibold}; color: ${props => props.positive ? theme.colors.success : theme.colors.error}; margin-top: ${theme.spacing.sm};`;
const ChartContainer = styled.div`background: ${theme.colors.surface}50; border-radius: ${theme.borderRadius.md}; padding: ${theme.spacing.lg}; height: 250px; display: flex; align-items: center; justify-content: center; color: ${theme.colors.text}60;`;
const BarChart = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing.md};`;
const BarRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing.md};`;
const BarLabel = styled.div`width: 120px; font-size: ${theme.typography.sizes.sm}; color: ${theme.colors.text}80;`;
const BarContainer = styled.div`flex: 1; height: 32px; background: ${theme.colors.surface}; border-radius: ${theme.borderRadius.md}; overflow: hidden; position: relative;`;
const BarFill = styled.div<{ percentage: number; color: string }>`height: 100%; width: ${props => props.percentage}%; background: ${props => props.color}; display: flex; align-items: center; justify-content: flex-end; padding-right: ${theme.spacing.sm}; transition: width 0.5s ease;`;
const BarValue = styled.div`font-size: ${theme.typography.sizes.xs}; font-weight: ${theme.typography.weights.bold}; color: white;`;

export const AdvancedAnalytics: React.FC = () => {
  const [period, setPeriod] = useState('month');

  const kpis = {
    totalScorings: { value: 1234, trend: 12.5, positive: true },
    avgScore: { value: 72.8, trend: -2.3, positive: false },
    completionRate: { value: 94.5, trend: 3.1, positive: true },
    avgDuration: { value: '2.3j', trend: -15, positive: true }
  };

  const profileDistribution = [
    { label: 'Salariés', value: 60, color: theme.colors.primary },
    { label: 'Entrepreneurs', value: 25, color: theme.colors.info },
    { label: 'Secteur Informel', value: 15, color: theme.colors.warning }
  ];

  const housingTypeDistribution = [
    { label: 'T1', value: 15, color: theme.colors.success },
    { label: 'T2', value: 35, color: theme.colors.primary },
    { label: 'T3', value: 30, color: theme.colors.info },
    { label: 'T4', value: 15, color: theme.colors.warning },
    { label: 'T5+', value: 5, color: theme.colors.error }
  ];

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <div>
            <Title>Analytics Avancés</Title>
            <Subtitle>Business Intelligence et rapports personnalisés</Subtitle>
          </div>
          <FilterBar>
            <FiCalendar />
            <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="week">Cette semaine</option>
              <option value="month">Ce mois</option>
              <option value="quarter">Ce trimestre</option>
              <option value="year">Cette année</option>
            </Select>
            <Button onClick={() => alert('Exporter rapport')}><FiDownload /> Exporter</Button>
          </FilterBar>
        </Header>

        <Grid>
          <Card>
            <CardTitle><FiBarChart2 /> Scorings Totaux</CardTitle>
            <StatValue>{kpis.totalScorings.value.toLocaleString('fr-FR')}</StatValue>
            <StatLabel>Candidatures traitées</StatLabel>
            <Trend positive={kpis.totalScorings.positive}><FiTrendingUp /> {kpis.totalScorings.positive ? '+' : ''}{kpis.totalScorings.trend}% vs période précédente</Trend>
          </Card>

          <Card>
            <CardTitle><FiActivity /> Score Moyen</CardTitle>
            <StatValue>{kpis.avgScore.value}/100</StatValue>
            <StatLabel>Toutes candidatures</StatLabel>
            <Trend positive={kpis.avgScore.positive}><FiTrendingUp /> {kpis.avgScore.positive ? '+' : ''}{kpis.avgScore.trend}% vs période précédente</Trend>
          </Card>

          <Card>
            <CardTitle><FiPieChart /> Taux de Complétion</CardTitle>
            <StatValue>{kpis.completionRate.value}%</StatValue>
            <StatLabel>Dossiers complétés</StatLabel>
            <Trend positive={kpis.completionRate.positive}><FiTrendingUp /> {kpis.completionRate.positive ? '+' : ''}{kpis.completionRate.trend}% vs période précédente</Trend>
          </Card>

          <Card>
            <CardTitle><FiCalendar /> Durée Moyenne</CardTitle>
            <StatValue>{kpis.avgDuration.value}</StatValue>
            <StatLabel>Du dépôt à l'attribution</StatLabel>
            <Trend positive={kpis.avgDuration.positive}><FiTrendingUp /> {kpis.avgDuration.positive ? '+' : ''}{kpis.avgDuration.trend}% vs période précédente</Trend>
          </Card>
        </Grid>

        <Grid>
          <Card>
            <CardTitle><FiPieChart /> Distribution des Profils</CardTitle>
            <BarChart>
              {profileDistribution.map((item, idx) => (
                <BarRow key={idx}>
                  <BarLabel>{item.label}</BarLabel>
                  <BarContainer>
                    <BarFill percentage={item.value} color={item.color}>
                      <BarValue>{item.value}%</BarValue>
                    </BarFill>
                  </BarContainer>
                </BarRow>
              ))}
            </BarChart>
          </Card>

          <Card>
            <CardTitle><FiBarChart2 /> Types de Logements Demandés</CardTitle>
            <BarChart>
              {housingTypeDistribution.map((item, idx) => (
                <BarRow key={idx}>
                  <BarLabel>{item.label}</BarLabel>
                  <BarContainer>
                    <BarFill percentage={item.value} color={item.color}>
                      <BarValue>{item.value}%</BarValue>
                    </BarFill>
                  </BarContainer>
                </BarRow>
              ))}
            </BarChart>
          </Card>
        </Grid>

        <Card>
          <CardTitle><FiTrendingUp /> Évolution des Scorings (Mock)</CardTitle>
          <ChartContainer>[Graphique en ligne - Évolution mensuelle]</ChartContainer>
        </Card>
      </Container>
    </DashboardLayout>
  );
};
